import React, { CSSProperties } from 'react'
import { AgTechQueryRun, queryResult } from '@agtech/core/data/queries/AgTechDataQueries';
import AgTechQueryLoadingSurface from '@agtech/core/data/queries/AgTechQueryLoadingSurface';

export enum AgTechCardStyle {
    Standard,
    Formal,
    SurfaceBody
}

export type AgTechCardLoadingConfig = {
    refreshWhenChanged: string,
    waitUntilLoaded: boolean,
    loadingText?: string
}

export type AgTechCoreCardProps<TCardData = any> = {
    children?: React.ReactNode | ((data: TCardData) => React.ReactNode),
    content?: {
        header?: (data: TCardData) => React.ReactNode,
        footer?: (data: TCardData) => React.ReactNode
    },
    data?: {
        query?: AgTechQueryRun<TCardData>
    },
    structure?: {
        height?: number,
        width?: number,
    },
    design?: {
        stripe?: string,
        style?: AgTechCardStyle
    },
    classes?: {
        card?: string,
        header?: string,
        body?: string,
        footer?: string
    }
}

export type AgTechCardProps<TCardData> = AgTechCoreCardProps<TCardData>;

const AgTechCard = <TCardData, >(props: AgTechCardProps<TCardData>) => {
    const cardQuery = props.data?.query ?? queryResult({} as any);
    const cardClasses = props?.classes?.card ?? '';
    const cardHeaderClasses = props?.classes?.header ?? '';
    const cardBodyClasses = props?.classes?.body ?? '';
    const cardFooterClasses = props?.classes?.footer ?? '';

    const cardStripeClasses: CSSProperties = props.design?.stripe ? {
        borderLeftWidth: 3,
        borderLeftColor: props.design?.stripe
    } : {};

    return (
        <AgTechQueryLoadingSurface
            query={cardQuery}
            appearance={{
                classes: `bordered rounded bg-white row-based p-3 ${cardClasses} flex`,
                styles: {
                    height: props.structure?.height,
                    width: props.structure?.width,
                    ...cardStripeClasses
                }
            }}
            loading={{
                showLoader: false
            }}
        >
            {(data) => (
                <>
                    {props.content?.header && (
                        <div className={`column-based-vertically-centered wide pb-4 ${cardHeaderClasses} flex`}>
                            {props.content.header(data)}
                        </div>
                    )}

                    { props.children && (
                        <div className={`flexed scrollable scrollbar-hidden row-based ${cardBodyClasses} flex`}>
                            {typeof props.children === 'function' ? props.children(data) : props.children}
                        </div>
                    )}

                    {props.content?.footer && (
                        <div className={`column-based-vertically-centered wide mt-auto flex ${cardFooterClasses}`}>
                            {props.content.footer(data)}
                        </div>
                    )}
                </>
            )}
        </AgTechQueryLoadingSurface>
    )
};

export default AgTechCard;